import { FormEvent, useState } from 'react';

interface LeadCaptureFormProps {
  onSubmit: (name: string, email: string) => Promise<void>;
  onBack: () => void;
  isSubmitting: boolean;
  error: string | null;
}

export function LeadCaptureForm({
  onSubmit,
  onBack,
  isSubmitting,
  error,
}: LeadCaptureFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; email?: string }>({});

  function validate(): boolean {
    const errors: { name?: string; email?: string } = {};
    if (!name.trim()) errors.name = 'Name is required';
    if (!email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errors.email = 'Enter a valid email address';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit(name.trim(), email.trim());
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-cream/40 p-6 sm:p-8">
      <div className="mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-cream/20 rounded-full mb-4">
          <span className="text-2xl" role="img" aria-label="results">
            🎯
          </span>
        </div>
        <h2 className="text-2xl font-bold text-brand">Your results are ready!</h2>
        <p className="mt-2 text-brand-slate">
          Enter your details to receive your personalized Social Style profile and insights from
          our consulting team.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-brand mb-1">
            Full name
          </label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSubmitting}
            className={`
              w-full px-4 py-2.5 rounded-lg border text-brand
              focus:outline-none focus:ring-2 focus:ring-scarlet/40 focus:border-transparent
              disabled:bg-porcelain disabled:text-brand-slate
              ${fieldErrors.name ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'}
            `}
            placeholder="Jane Smith"
          />
          {fieldErrors.name && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-brand mb-1">
            Work email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
            className={`
              w-full px-4 py-2.5 rounded-lg border text-brand
              focus:outline-none focus:ring-2 focus:ring-scarlet/40 focus:border-transparent
              disabled:bg-porcelain disabled:text-brand-slate
              ${fieldErrors.email ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'}
            `}
            placeholder="jane@yourcompany.com"
          />
          {fieldErrors.email && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="
            w-full py-3 px-6 rounded-lg bg-scarlet text-white font-semibold
            hover:bg-scarlet-dark active:bg-scarlet-dark
            focus:outline-none focus:ring-2 focus:ring-scarlet/40 focus:ring-offset-2
            disabled:opacity-60 disabled:cursor-not-allowed
            transition-colors duration-150
          "
        >
          {isSubmitting ? 'Submitting…' : 'See my results →'}
        </button>
      </form>

      <div className="mt-4 text-center">
        <button
          onClick={onBack}
          disabled={isSubmitting}
          className="text-sm text-brand-slate hover:text-brand transition-colors disabled:opacity-50"
        >
          ← Go back
        </button>
      </div>

      <p className="mt-6 text-xs text-brand-slate/60 text-center">
        Your information is kept confidential and will not be shared with third parties.
      </p>
    </div>
  );
}
